import { lazy, Suspense, useEffect, useRef, type ReactNode } from "react";
import { AppHeader } from "./components/AppHeader";
import { ChallengeProgress } from "./components/ChallengeProgress";
import { Footer } from "./components/Footer";
import { IntroOverlay } from "./components/IntroOverlay";
import { NextCandleView } from "./components/NextCandleView";
import { StatusBar } from "./components/StatusBar";
import { TradeStats } from "./components/TradeStats";
import { TrendView } from "./components/TrendView";
import { startBankPrefetch } from "./lib/prefetch";
import { BASELINE } from "./lib/presets";
import { nextCandleOf } from "./lib/resolve";
import { matchPackRoute, useRoute } from "./lib/router";
// NotFoundPage is tiny and route-critical (a mistyped URL should never show
// a loading flicker before "not found"), so it stays a static import.
import { NotFoundPage } from "./pages/NotFoundPage";
import { useActiveLoop, type LoopId, type ScoreSource } from "./state/loops";
import { usePredictDailyStore } from "./state/predictDailyStore";
import { usePredictMonthlyStore } from "./state/predictMonthlyStore";
import { usePredictDailyNextStore } from "./state/predictNextCandleStores";
import {
  usePredictMonthlyTrendStore,
  usePredictWeeklyTrendStore,
} from "./state/predictTrendStores";
import { usePredictWeeklyStore } from "./state/predictWeeklyStore";

// Split out of the entry chunk (GRA code-splitting pass): none of these are
// needed to render the opening Round, which is always a Predict Loop.
// `ChallengeProgress` stays a static import above since it is the header
// stats for a Loop that itself lazy-loads below, not the heavy view.
const ChallengeView = lazy(() =>
  import("./components/ChallengeView").then((m) => ({ default: m.ChallengeView })),
);
const TradeView = lazy(() =>
  import("./components/TradeView").then((m) => ({ default: m.TradeView })),
);
const DataPage = lazy(() => import("./pages/DataPage").then((m) => ({ default: m.DataPage })));
const HowToPlayPage = lazy(() =>
  import("./pages/HowToPlayPage").then((m) => ({ default: m.HowToPlayPage })),
);
const PrivacyPage = lazy(() =>
  import("./pages/PrivacyPage").then((m) => ({ default: m.PrivacyPage })),
);
const LearnPage = lazy(() => import("./pages/LearnPage").then((m) => ({ default: m.LearnPage })));
// The practice pack: a page nobody reaches on the way to a Round, and an
// exercise view that pulls the whole chart stack in behind it, so both stay
// out of the entry chunk exactly as the static pages do.
const PackPage = lazy(() => import("./pages/PackPage").then((m) => ({ default: m.PackPage })));
const PackExercisePage = lazy(() =>
  import("./pages/PackExercisePage").then((m) => ({ default: m.PackExercisePage })),
);

/** Minimal muted mono line, consistent with the loading text every Loop's
 * view already shows (e.g. TrendView's "loading rounds…") while its bank
 * fetches. Never a spinner. */
function PageFallback() {
  return <p className="p-10 text-sm text-muted">loading…</p>;
}

/** The static pages reachable by URL and from the footer (GRA-153). */
const PAGES: Record<string, ReactNode> = {
  "/how-to-play": (
    <Suspense fallback={<PageFallback />}>
      <HowToPlayPage />
    </Suspense>
  ),
  "/data": (
    <Suspense fallback={<PageFallback />}>
      <DataPage />
    </Suspense>
  ),
  "/learn": (
    <Suspense fallback={<PageFallback />}>
      <LearnPage />
    </Suspense>
  ),
  "/privacy": (
    <Suspense fallback={<PageFallback />}>
      <PrivacyPage />
    </Suspense>
  ),
};

/**
 * Top-level switcher (spec §4 modes, ADR-0006). Lazily loads the active Loop's
 * bank the first time it is selected, and renders the shared AppHeader (GRA-94)
 * above the Loop's view: a Predict (timeframe × horizon) pair, Trade, or the
 * Daily challenge.
 */

/** One entry per Loop id, so a Loop's view can never be missing or mismatched. */
const VIEWS: Record<LoopId, ReactNode> = {
  "predict:daily:trend": (
    <TrendView store={usePredictDailyStore} loadingMessage="loading rounds…" />
  ),
  "predict:daily:nextCandle": (
    <NextCandleView
      store={usePredictDailyNextStore}
      loadingMessage="loading rounds…"
      answerCandle={nextCandleOf}
      markets="from-bank"
    />
  ),
  "predict:weekly:trend": (
    <TrendView store={usePredictWeeklyTrendStore} loadingMessage="loading weekly rounds…" />
  ),
  "predict:weekly:nextCandle": (
    <NextCandleView
      store={usePredictWeeklyStore}
      loadingMessage="loading weekly rounds…"
      answerCandle={nextCandleOf}
      markets="from-bank"
    />
  ),
  "predict:monthly:trend": (
    <TrendView store={usePredictMonthlyTrendStore} loadingMessage="loading monthly rounds…" />
  ),
  "predict:monthly:nextCandle": (
    <NextCandleView
      store={usePredictMonthlyStore}
      loadingMessage="loading monthly rounds…"
      answerCandle={nextCandleOf}
      markets="from-bank"
    />
  ),
  trade: (
    <Suspense fallback={<PageFallback />}>
      <TradeView />
    </Suspense>
  ),
  challenge: (
    <Suspense fallback={<PageFallback />}>
      <ChallengeView />
    </Suspense>
  ),
};

/**
 * The header stats of a scored Loop (GRA-94). Its own component so the two
 * store subscriptions stay unconditional: Trade has no hit-rate scoreboard, and
 * App would otherwise have to call a Loop hook only sometimes. Remounted per
 * Loop by its key, so switching Loop starts a clean subscription.
 */
function ScoredStats({ source, scopeLabel }: { source: ScoreSource; scopeLabel: string }) {
  const score = source((s) => s.score);
  const streak = source((s) => s.streak);
  return <StatusBar score={score} streak={streak} baseline={BASELINE} scopeLabel={scopeLabel} />;
}

export default function App() {
  const route = useRoute();
  const activeLoop = useActiveLoop();
  const loop = activeLoop.id;

  // Load each loop's bank exactly once, the first time it becomes active.
  const initialized = useRef(new Set<string>());
  useEffect(() => {
    if (initialized.current.has(loop)) return;
    initialized.current.add(loop);
    void activeLoop.init().then(() => {
      // If the load failed, forget it so re-selecting the loop retries.
      if (activeLoop.status() === "error") {
        initialized.current.delete(loop);
        return;
      }
      // The opening Round is interactive, so the network is free: warm the
      // indexes the player has not picked yet during idle time, so switching
      // timeframe later is a cache read instead of an index fetch on top of a
      // shard fetch. `startBankPrefetch` itself waits for the page's own
      // `load` event before it asks for anything, which is what keeps these
      // requests out of the initial critical chain rather than racing it
      // (lib/prefetch.ts owns that gate, the ordering, and the metered-
      // connection gate).
      void startBankPrefetch();
    });
  }, [loop, activeLoop]);

  // Three stats rows, one per kind of scoreboard: a hit rate (Predict), an
  // Account balance (Trade), and a run's progress (the Daily challenge, whose
  // score belongs to the run and is announced on its end screen).
  const stats = activeLoop.scoreboard ? (
    <ScoredStats
      key={loop}
      source={activeLoop.scoreboard}
      scopeLabel={activeLoop.scopeLabel ?? ""}
    />
  ) : loop === "challenge" ? (
    <ChallengeProgress />
  ) : (
    <TradeStats />
  );

  // The three static pages and the not-found view are rendered outside the
  // game shell entirely (GRA-153): they are reference pages, not a Loop, so
  // they skip AppHeader/IntroOverlay rather than trying to fit inside a
  // layout built for the keyboard-first game loop. All hooks above still run
  // on every route, so returning players land back on the game with no lost
  // state the moment they navigate to "/".
  if (route !== "/") {
    const page = PAGES[route];
    if (page) return page;
    // `/packs/<pack>` and `/packs/<pack>/<exercise>`: the one route with a
    // variable in it, so it is matched rather than looked up in PAGES. An
    // unknown pack id is a not-found like any other unknown path; an unknown
    // exercise id is the pack's own question, answered on the pack's page.
    const pack = matchPackRoute(route);
    if (pack?.packId === "pullbacks") {
      return (
        <Suspense fallback={<PageFallback />}>
          {pack.exerciseId ? <PackExercisePage exerciseId={pack.exerciseId} /> : <PackPage />}
        </Suspense>
      );
    }
    return <NotFoundPage />;
  }

  return (
    // `h-screen` from `sm` up: the desktop loop is one non-scrolling screen.
    // Below it the header stats, the settings strip and the trade panel can
    // together leave the chart a few dozen pixels tall, so the shell is only
    // *at least* a screen and the page scrolls past the chart's floor instead.
    // `overflow-x-clip` is the safety net index.css aims at with
    // `body { overflow-x: hidden }`, which stops working the moment the page
    // scrolls vertically: an absolutely-positioned hint (a Tooltip panel next
    // to the right edge) then widens the document and the phone pans sideways.
    // `clip` on this axis leaves the vertical axis `visible`, so tooltips still
    // hang below their trigger.
    <div className="flex min-h-screen flex-col overflow-x-clip sm:h-screen">
      <AppHeader>{stats}</AppHeader>
      {VIEWS[loop]}
      <IntroOverlay />
      <Footer />
    </div>
  );
}
